import { getApiUrl } from "@utils/baseUrl";
import { ERROR_MSG } from "@utils/enum";
import { useDebounce } from "@utils/utils";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { companySearchTabs, searchTypeCustomStyles } from "../enum";

const categoryList = [
  { label: "Suppliers of", value: "export" },
  { label: "Buyers of", value: "import" },
];

const CloseIcon = ({ size = "20px", color = "#101828" }) => (
  <svg className="tw-cursor-pointer" xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="m291-240-51-51 189-189-189-189 51-51 189 189 189-189 51 51-189 189 189 189-51 51-189-189-189 189Z" />
  </svg>
);

const MAX_SELECTIONS = 1;
const MAX_TO_SHOW = 1;

const extractHSCodeSafe = (label) => {
  try {
    const codePart = label?.split("-")?.[0]?.split(":")?.[1]?.slice(0, -1);
    if (codePart) {
      return codePart.trim();
    }
    return label?.trim() || "";
  } catch {
    return label?.trim() || "";
  }
};

const CompanySearchCard = ({ activeTab = companySearchTabs.COMPANY, onSearch, selectedItems, setSelectedItems, setSearchInput, searchInput, resetSearch, initialTradeDirection, searchedTradeDirection, onReloadRecentSearch, onTrackTradeDirectionChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [autoSuggestList, setAutoSuggestList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tradeDirection, setTradeDirection] = useState(initialTradeDirection || categoryList[0]);
  const [recentSearchSuggestions, setRecentSearchSuggestions] = useState(null);

  // Reloads (recent/saved) start a search without going through this card, so
  // keep the dropdown in step with the direction the page actually searched.
  useEffect(() => {
    if (searchedTradeDirection?.value) setTradeDirection(searchedTradeDirection);
  }, [searchedTradeDirection?.value]); // eslint-disable-line react-hooks/exhaustive-deps
  const selectedParentCategory = selectedItems?.[0]?.category || null;
  const trimmedSearchInput = searchInput?.trim() || "";

  const debouncedValue = useDebounce(trimmedSearchInput, 300);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const filterSelectedItems = (list) => {
    if (!selectedParentCategory || selectedItems.length === 0) return list;

    const selectedLabels = selectedItems.map((item) => item.label.toLowerCase());

    if (Array.isArray(list) && list[0]?.category && list[0]?.items) {
      return list
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            const label = typeof item === "string" ? item : item.label;
            return !selectedLabels.includes(label.toLowerCase());
          }),
        }))
        .filter((group) => group.items.length > 0);
    } else {
      return list.filter((item) => {
        const label = typeof item === "string" ? item : item.label;
        return !selectedLabels.includes(label.toLowerCase());
      });
    }
  };

  const fetchRecentSearchSuggestions = async () => {
    try {
      const { data } = await axios.post(getApiUrl("getAutoSuggestRecentSearches"), { search_source: "product_search" });
      setRecentSearchSuggestions(data?.data || {});
    } catch {
      setRecentSearchSuggestions({});
    }
  };

  // Every search re-logs a recent-search entry on the backend, so invalidate the
  // cached suggestion list here - the next input focus will fetch it fresh.
  const triggerSearch = (items, direction, options) => {
    onSearch(items, direction, options);
    setRecentSearchSuggestions(null);
  };

  // Delegates the actual reload to the same reloadRecentSearch used by the
  // empty-state "Reload" button, so both paths always stay in lockstep -
  // only the local, card-private UI (trade-direction toggle, autosuggest
  // list, dropdown open state) is reset here.
  const handleRecentSearchClick = (suggestion) => {
    if (!onReloadRecentSearch) return;
    const direction = suggestion.company_type === "shipper" ? { label: "Suppliers of", value: "export" } : { label: "Buyers of", value: "import" };
    setTradeDirection(direction);
    setAutoSuggestList([]);
    setIsOpen(false);
    setRecentSearchSuggestions(null);
    onReloadRecentSearch(suggestion);
  };

  const handleAutoSuggest = async (value) => {
    if (!value || value.length < 2) {
      setAutoSuggestList([]);
      return;
    }
    setLoading(true);

    try {
      const lowerCaseInput = value.toLowerCase();
      if (activeTab === companySearchTabs.PRODUCT) {
        if (selectedParentCategory) {
          if (selectedParentCategory === "Product") {
            const { data } = await axios.get(getApiUrl("getAutosuggestResultForProductKeyword"), { params: { search: lowerCaseInput, lang: "EN" } });
            const list = data?.suggestions?.map((item) => ({ ...item, category: selectedParentCategory })) || [];
            setAutoSuggestList(filterSelectedItems(list));
          } else if (selectedParentCategory === "HS Code") {
            const { data } = await axios.get(getApiUrl("getAutosuggestResult"), {
              params: { search: lowerCaseInput, country: "multicountry" },
            });
            const list =
              data?.suggestions?.map((label) => ({
                label,
                value: extractHSCodeSafe(label),
                category: selectedParentCategory,
              })) || [];
            setAutoSuggestList(filterSelectedItems(list));
          }
        } else {
          const params = {
            data_source: tradeDirection?.value || "import",
            search: lowerCaseInput,
            tab: companySearchTabs.COMPANY,
          };
          const { data } = await axios.get(getApiUrl("allCategoryAutoSuggest"), { params });

          const grouped = [];
          if (data?.product_description?.length) {
            grouped.push({
              category: "Product",
              items: data.product_description.map((item) => ({ ...item, category: "Product" })),
            });
          }
          if (data?.product_hscode?.length) {
            grouped.push({
              category: "HS Code",
              items: data.product_hscode.map((label) => ({ label, value: extractHSCodeSafe(label), category: "HS Code" })),
            });
          }

          setAutoSuggestList(grouped);
        }
      }

      if (activeTab === companySearchTabs.COMPANY) {
        const params = { hint: lowerCaseInput, tab: companySearchTabs.COMPANY };
        const { data } = await axios.get(getApiUrl("companyProfiles/getCompanyAutoSuggest"), { params });
        setAutoSuggestList(data?.data || []);
      }
    } catch (error) {
      toast.error(ERROR_MSG);
      setAutoSuggestList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debouncedValue === "" || debouncedValue === null) {
      setAutoSuggestList([]);
      setLoading(false);
    } else if (debouncedValue.length > 1) {
      handleAutoSuggest(trimmedSearchInput);
    }
  }, [debouncedValue]);

  const onInputChange = (e) => {
    const value = e.target.value.replace(/\\/g, "");
    setSearchInput(value);
    if (!isOpen) setIsOpen(true);
    setLoading(value?.trim()?.length >= 2);
  };

  const normalizeValue = (item) => {
    const label = item?.label?.toString()?.trim() || "";
    const value = item?.value?.toString()?.trim() || "";
    const normalizedHS = item?.category === "HS Code" ? extractHSCodeSafe(label || value) : value || label;
    return normalizedHS.toLowerCase();
  };

  const isDuplicateItem = (item, list) => {
    const normalizedNew = normalizeValue(item);
    return list.some((s) => normalizeValue(s) === normalizedNew);
  };

  const onSelect = (item) => {
    if (activeTab === companySearchTabs.COMPANY) {
      setSelectedItems([item]);
      setSearchInput(item.label);
      setAutoSuggestList([]);
      setIsOpen(false);
      triggerSearch([item], null);
      return;
    }

    if (activeTab === companySearchTabs.PRODUCT && !selectedParentCategory) {
      if (item.category === "HS Code") {
        const code = extractHSCodeSafe(item.label);
        item = { ...item, value: code, label: code };
      }
      setSelectedItems([item]);
      setSearchInput("");
      setAutoSuggestList([]);
      setIsOpen(false);
      triggerSearch([item], tradeDirection);
      return;
    }

    if (selectedItems.length >= MAX_SELECTIONS) {
      toast.warning(`Maximum ${MAX_SELECTIONS} selections allowed`);
      return;
    }

    let newItem = { ...item };
    if (item.category === "HS Code" || selectedParentCategory === "HS Code") {
      const code = extractHSCodeSafe(item.label);
      newItem = { ...item, value: code, label: code };
    }

    if (isDuplicateItem(newItem, selectedItems)) return;

    setSelectedItems([...selectedItems, newItem]);
    setSearchInput("");
    setAutoSuggestList([]);
    setLoading(false);
    if (isOpen && inputRef.current) inputRef.current.focus();
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "Product":
        return "tw-bg-red-500";
      case "HS Code":
        return "tw-bg-blue-500";
      default:
        return "tw-bg-gray-500";
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "Product":
        return "P";
      case "HS Code":
        return "H";
      default:
        return "?";
    }
  };

  const changeTradeDirection = (value) => {
    setTradeDirection(value);
    setSelectedItems([]);
    setSearchInput("");
    setAutoSuggestList([]);
    resetSearch();
    onTrackTradeDirectionChange?.(value);
  };

  const removeParentCatgory = () => setSelectedItems([]);
  const removeSelection = (val) => setSelectedItems(selectedItems.filter((s) => s.label !== val.label));

  const handleSearchClick = () => {
    const tradeDir = activeTab === companySearchTabs.PRODUCT ? tradeDirection : null;

    if (activeTab === companySearchTabs.COMPANY || (activeTab === companySearchTabs.PRODUCT && !selectedParentCategory)) {
      const selected = [{ label: trimmedSearchInput, value: trimmedSearchInput }];
      triggerSearch(selected, tradeDir);
      setSelectedItems(selected);
      setIsOpen(false);
      return;
    }

    if (trimmedSearchInput) {
      const inValidHS = selectedParentCategory === "HS Code" && !/^\d+$/.test(trimmedSearchInput);

      const newItem = {
        label: trimmedSearchInput,
        value: trimmedSearchInput,
        category: selectedParentCategory,
      };

      if (isDuplicateItem(newItem, selectedItems) || inValidHS) {
        setSearchInput("");
        setAutoSuggestList([]);
        setIsOpen(false);
        triggerSearch(selectedItems, tradeDir);
        return;
      }

      if (selectedItems.length >= MAX_SELECTIONS) {
        toast.warning(`Maximum ${MAX_SELECTIONS} selections allowed`);
        return;
      }

      const search = [...selectedItems, newItem];
      setSelectedItems(search);
      triggerSearch(search, tradeDir);
      setSearchInput("");
    } else {
      triggerSearch(selectedItems, tradeDir);
    }

    setIsOpen(false);
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.target.value === "") {
        return;
      }
      handleSearchClick();
      if (inputRef.current) inputRef.current.blur();
    }

    if (e.key === "Tab" && activeTab === companySearchTabs.PRODUCT && selectedParentCategory && trimmedSearchInput) {
      e.preventDefault();

      if (selectedItems.length >= MAX_SELECTIONS) {
        toast.warning(`Maximum ${MAX_SELECTIONS} selections allowed`);
        return;
      }

      const inValidHS = selectedParentCategory === "HS Code" && !/^\d+$/.test(trimmedSearchInput);

      const newItem = {
        label: trimmedSearchInput,
        value: trimmedSearchInput,
        category: selectedParentCategory,
      };

      if (!isDuplicateItem(newItem, selectedItems) && !inValidHS) {
        setSelectedItems([...selectedItems, newItem]);
      }

      setSearchInput("");
      setAutoSuggestList([]);
      if (inputRef.current) inputRef.current.focus();
    }

    setLoading(false);
  };

  const renderSuggestions = () => {
    if (!isOpen) return null;
    return (
      <div className="tw-absolute tw-z-10 tw-bg-white tw-w-full tw-border tw-rounded-xl tw-mt-2 tw-shadow-lg tw-max-h-96 tw-overflow-y-auto">
        {activeTab === companySearchTabs.PRODUCT && selectedParentCategory && selectedItems.length > 0 && (
          <div className="tw-p-3 tw-border-b tw-border-gray-200 tw-sticky tw-top-0 tw-z-10 tw-bg-white">
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
              <p className="tw-text-xs tw-font-medium tw-text-gray-600">
                Selected ({selectedItems.length}/{MAX_SELECTIONS})
              </p>
              <span onClick={() => setSelectedItems([])} className="tw-cursor-pointer tw-text-xs tw-text-primary-purple-600 hover:tw-text-primary-purple-700 tw-font-medium">
                Clear all
              </span>
            </div>
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {selectedItems.map((item) => (
                <span key={item.label} className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-white tw-border tw-border-primary-purple-600 tw-rounded-full tw-px-2 tw-py-0.5">
                  <span className="tw-text-xs tw-text-gray-700">{item.label}</span>
                  <div onClick={() => removeSelection(item)} className="tw-cursor-pointer hover:tw-opacity-70">
                    <CloseIcon size="14px" />
                  </div>
                </span>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <div className="tw-px-4 tw-py-8 tw-text-center">
            <div className="tw-inline-block tw-w-8 tw-h-8 tw-border-4 tw-border-gray-200 tw-border-t-primary-purple-600 tw-rounded-full tw-animate-spin tw-mb-3" />
            <p className="tw-text-sm tw-text-gray-600">Searching...</p>
          </div>
        )}

        {!loading && !trimmedSearchInput && (
          <>
            <div className="tw-px-4 tw-py-8 tw-text-center">
              <p className="tw-text-sm tw-text-gray-600 tw-font-medium">Start typing to see suggestions</p>
            </div>
            {activeTab === companySearchTabs.PRODUCT && recentSearchSuggestions?.["All Categories"]?.length > 0 && (
              <div className="tw-p-3 tw-border-t tw-border-gray-200">
                <div className="tw-text-xs tw-text-gray-900 tw-mb-2">Recent Search</div>
                <div className="tw-flex tw-gap-2 tw-flex-wrap">
                  {recentSearchSuggestions["All Categories"].map((suggestion) => (
                    <div key={`${suggestion.user_search}-${suggestion.category_name}-${suggestion.company_type}`} className="tw-cursor-pointer tw-px-2 tw-py-1 tw-border tw-border-gray-200 tw-rounded-[54px] tw-shadow-autoSuggestPill tw-bg-white tw-flex tw-items-center tw-gap-1 tw-text-sm tw-text-gray-900" onClick={() => handleRecentSearchClick(suggestion)}>
                      <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#1c1b1f">
                        <path d="M813-144 574-383q-30 22-66 34.5T432-336q-37 0-70-10.5T300-376l53-53q18 9 37.5 15t41.5 6q70 0 119-49t49-119q0-70-49-119t-119-49q-70 0-119 49t-49 119l53-51 51 51-136 136L96-576l51-51 45 46q2-98 71.5-166.5T432-816q100 0 170 70t70 170q0 40-12.5 76T625-434l239 239-51 51Z" />
                      </svg>
                      <span>
                        {suggestion.category_name} - {suggestion.search?.join(", ")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!loading && trimmedSearchInput.length > 1 && autoSuggestList.length === 0 && (
          <div className="tw-px-4 tw-py-8 tw-text-center">
            <p className="tw-text-sm tw-text-gray-600 tw-font-medium">No suggestions found</p>
          </div>
        )}

        {!loading && trimmedSearchInput.length <= 1 && trimmedSearchInput && (
          <div className="tw-px-4 tw-py-8 tw-text-center">
            <p className="tw-text-sm tw-text-gray-600 tw-font-medium">Enter at least 2 characters to see suggestions</p>
          </div>
        )}

        {!loading && autoSuggestList.length > 0 && (
          <>
            {autoSuggestList[0]?.category && autoSuggestList[0]?.items ? (
              <>
                {autoSuggestList.map((group) => (
                  <div key={group.category} className="tw-border-b tw-border-gray-200 last:tw-border-b-0">
                    <div className="tw-flex tw-items-center tw-justify-between tw-bg-gray-50 tw-px-4 tw-py-2">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        <span className={`${getCategoryColor(group.category)} tw-text-white tw-text-xs tw-font-bold tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center`}>{getCategoryIcon(group.category)}</span>
                        <span className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase">{group.category}</span>
                      </div>
                    </div>

                    {group.items.map((item, i) => {
                      const label = typeof item === "string" ? item : item.label;
                      const regex = new RegExp(`(${trimmedSearchInput.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
                      const highlighted = label.replace(regex, (m) => `<strong>${m}</strong>`);

                      return (
                        <div
                          key={`${group.category}-${label}-${i}`}
                          onClick={() =>
                            onSelect({
                              label,
                              value: label,
                              category: group.category,
                            })
                          }
                          className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-800 tw-cursor-pointer hover:tw-bg-purple-50 tw-border-b tw-border-gray-100 last:tw-border-b-0"
                          dangerouslySetInnerHTML={{ __html: highlighted }}
                        />
                      );
                    })}
                  </div>
                ))}
              </>
            ) : (
              <div>
                {autoSuggestList.map((item, i) => {
                  const label = typeof item === "string" ? item : item.label;
                  const regex = new RegExp(`(${trimmedSearchInput.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
                  const highlighted = label.replace(regex, (m) => `<strong>${m}</strong>`);
                  return <div key={`${item.label}-${i}`} onClick={() => onSelect(item)} className="tw-px-4 tw-py-2 tw-cursor-pointer hover:tw-bg-purple-50 tw-text-sm tw-text-gray-900 tw-border-b tw-border-gray-100 last:tw-border-b-0" dangerouslySetInnerHTML={{ __html: highlighted }} />;
                })}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div ref={wrapperRef} className="tw-relative tw-w-[950px]">
      <div className="tw-bg-white tw-rounded-[85px] tw-border tw-border-gray-200 tw-flex tw-items-center tw-p-3 tw-pl-6 tw-shadow-[0_2px_4px_0_#eaeaea] tw-min-h-16">
        {activeTab === companySearchTabs.PRODUCT && (
          <div className="tw-border-r tw-border-gray-200 tw-self-stretch tw-flex tw-items-center">
            <Select onFocus={() => setIsOpen(false)} options={categoryList} isSearchable={false} styles={searchTypeCustomStyles} value={tradeDirection} onChange={changeTradeDirection} />
          </div>
        )}

        <div className={`tw-flex-1 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-self-stretch ${activeTab === companySearchTabs.PRODUCT ? "tw-pl-4" : ""}`}>
          {activeTab === companySearchTabs.PRODUCT && selectedParentCategory && (
            <div className="tw-py-1 tw-pl-1 tw-pr-2 tw-flex tw-items-center tw-gap-1 tw-rounded-[54px] tw-bg-gray-900 tw-shadow-autoSuggestPill tw-text-white tw-self-center">
              <span className={`${getCategoryColor(selectedParentCategory)} tw-text-white tw-text-xs tw-font-bold tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center`}>{getCategoryIcon(selectedParentCategory)}</span>
              <span className="tw-text-sm">{selectedParentCategory}</span>
              <span onClick={removeParentCatgory}>
                <CloseIcon size="16px" color="#ffffff" />
              </span>
            </div>
          )}

          <div onClick={() => setIsOpen(true)} className={`tw-flex tw-items-center tw-self-stretch ${activeTab === companySearchTabs.COMPANY || isOpen || selectedItems.length === 0 || !selectedParentCategory ? "tw-flex-1" : ""}`}>
            {(activeTab === companySearchTabs.COMPANY || isOpen || selectedItems.length === 0 || !selectedParentCategory) && (
              <input
                ref={inputRef}
                type="text"
                value={searchInput}
                onChange={onInputChange}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (activeTab === companySearchTabs.PRODUCT && trimmedSearchInput === "" && recentSearchSuggestions === null) fetchRecentSearchSuggestions();
                }}
                placeholder={activeTab === companySearchTabs.PRODUCT ? "Enter a product keyword or 6-digit HS code" : "Enter a company name, identifier, or keyword"}
                className="tw-flex-1 tw-min-w-[200px] tw-w-full tw-text-gray-700 tw-text-sm tw-font-medium tw-placeholder-gray-500 focus:tw-outline-none tw-bg-transparent"
              />
            )}
            {activeTab === companySearchTabs.PRODUCT && !isOpen && selectedItems.length > 0 && selectedParentCategory && (
              <div className="tw-text-xs tw-flex tw-gap-1 tw-items-center tw-self-center">
                {selectedItems.slice(0, MAX_TO_SHOW).map((item) => (
                  <div key={item.label} className="tw-flex tw-gap-1 tw-items-center tw-rounded-[54px] tw-border tw-border-primary-purple-600 tw-shadow-autoSuggestPill tw-py-0.5 tw-px-2">
                    <div className="tw-text-xs tw-text-gray-900 tw-text-nowrap tw-max-w-[120px] tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis">{item.label}</div>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        removeSelection(item);
                      }}>
                      <CloseIcon size="16px" />
                    </span>
                  </div>
                ))}
                {selectedItems.length > MAX_TO_SHOW && (
                  <div className="tw-flex tw-gap-1 tw-items-center tw-rounded-[54px] tw-border tw-border-primary-purple-600 tw-shadow-autoSuggestPill tw-py-0.5 tw-px-2">
                    <div className="tw-text-xs tw-text-gray-900 tw-text-nowrap tw-max-w-[120px] tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis">+{selectedItems.length - MAX_TO_SHOW}</div>
                  </div>
                )}
                {/* <span className="tw-flex tw-gap-1 tw-items-center tw-rounded-[54px] tw-border tw-border-gray-200 tw-cursor-pointer tw-shadow-autoSuggestPill tw-py-1 tw-px-2">+ Add</span> */}
              </div>
            )}
          </div>
        </div>

        <button onClick={handleSearchClick} disabled={!selectedParentCategory && !trimmedSearchInput} className="tw-cursor-pointer tw-bg-primary-purple-600 hover:tw-bg-primary-purple-700 tw-text-white tw-rounded-full tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-transition-colors tw-flex-shrink-0 tw-self-center disabled:tw-cursor-not-allowed disabled:tw-opacity-70">
          <svg xmlns="http://www.w3.org/2000/svg" height="16px" viewBox="0 -960 960 960" width="16px" fill="#fff">
            <path d="M784-120 532-372q-30 24-69 38t-83 14q-109 0-184.5-75.5T120-580q0-109 75.5-184.5T380-840q109 0 184.5 75.5T640-580q0 44-14 83t-38 69l252 252-56 56ZM380-400q75 0 127.5-52.5T560-580q0-75-52.5-127.5T380-760q-75 0-127.5 52.5T200-580q0 75 52.5 127.5T380-400Z" />
          </svg>
        </button>
      </div>

      {renderSuggestions()}
    </div>
  );
};

export default CompanySearchCard;
